import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { register } from "@/lib/api";
import { registerSchema, toRegisterPayload, type RegisterFormData } from "@/lib/validation";

const COUNTRY_CODES = [
  { code: "+39", label: "Italia (+39)" },
  { code: "+33", label: "Francia (+33)" },
  { code: "+49", label: "Germania (+49)" },
  { code: "+34", label: "Spagna (+34)" },
  { code: "+44", label: "UK (+44)" },
  { code: "+32", label: "Belgio (+32)" },
  { code: "+31", label: "Paesi Bassi (+31)" },
  { code: "+41", label: "Svizzera (+41)" },
  { code: "+43", label: "Austria (+43)" },
  { code: "+351", label: "Portogallo (+351)" },
  { code: "+48", label: "Polonia (+48)" },
  { code: "+1", label: "USA/Canada (+1)" },
];

type Props = {
  onSuccess?: () => void;
  onError?: (err: Error) => void;
};

export function RegisterForm({ onSuccess, onError }: Props) {
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: "onTouched",
    defaultValues: {
      companyName: "",
      referentName: "",
      email: "",
      countryCode: "+39",
      phone: "",
    },
  });

  async function onSubmit(data: RegisterFormData) {
    setSubmitError(null);
    try {
      const params = new URLSearchParams(window.location.search);
      const v = params.get("vertical");
      const vertical = v === "hospitality" ? "hospitality" : "restaurant";
      const payload = { ...toRegisterPayload(data), vertical };
      const { checkoutUrl } = await register(payload);
      window.location.href = checkoutUrl;
      onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Errore durante la registrazione";
      setSubmitError(message);
      onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="companyName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nome azienda</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Azienda S.r.l."
                  autoComplete="organization"
                  className="min-h-[44px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="referentName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nome referente</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Mario Rossi"
                  autoComplete="name"
                  className="min-h-[44px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

<FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="email"
                  placeholder="mario.rossi@azienda.it"
                  autoComplete="email"
                  className="min-h-[44px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex gap-2">
          <FormField
            control={form.control}
            name="countryCode"
            render={({ field }) => (
              <FormItem className="w-36 shrink-0">
                <FormLabel>Prefisso</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="min-h-[44px]">
                      <SelectValue placeholder="+" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {COUNTRY_CODES.map(({ code, label }) => (
                      <SelectItem key={code} value={code}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem className="min-w-0 flex-1">
                <FormLabel>Telefono</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="tel"
                    placeholder="333 1234567"
                    autoComplete="tel-national"
                    className="min-h-[44px]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {submitError && (
          <p className="text-destructive text-sm">{submitError}</p>
        )}
        <Button
          type="submit"
          size="lg"
          variant="default"
          className="w-full min-h-[44px] sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90"
          disabled={form.formState.isSubmitting}
        >
          {form.formState.isSubmitting ? "Registrazione..." : "Registrati"}
        </Button>
      </form>
    </Form>
  );
}
